import { plainToInstance } from 'class-transformer';
import moment from 'moment';
import { PlatformScheduleDTO } from '../dto/platform-schedule.dto';
import { PlatformScheduleAssetDTO } from '../dto/platform-schedule-asset.dto';
import { validateAllowedTypes, validateAspectRatio, validateFileSize, validateAspectRatioRange, validateMinMaxInclusive, validateMediaFileSize } from '../services/validation';
import { PlatformScheduleMetadataDTO } from '../dto/platform-schedule-metadata.dto';
import { kebabCaseString } from './stringHelpers';

/**
 * Method to load the local storage object for platform Schedule
 * @param localStorageKey
 * @param internalPlatformSchedule
 * @returns
 */
export const loadLocalStorage = (localStorageKey: string, internalPlatformSchedule: PlatformScheduleDTO) => {
  const storedData = localStorage.getItem(localStorageKey);

  const convertedData = storedData ? plainToInstance(PlatformScheduleDTO, JSON.parse(storedData)) : internalPlatformSchedule;

  if (convertedData.scheduleDate) {
    convertedData.scheduleDate = moment.isMoment(convertedData.scheduleDate) ? convertedData.scheduleDate : moment(convertedData.scheduleDate);
  }

  return convertedData;
};

/**
 * Method to load the local storage object for platform assets
 * @param localStorageKey
 * @param assetData
 * @returns
 */
export const loadLocalStorageAssets = (localStorageKey: string, assetData: PlatformScheduleAssetDTO[]) => {
  const storedAssets = localStorage.getItem(localStorageKey);

  return storedAssets ? JSON.parse(storedAssets) : assetData;
};

/**
 * Method to load the local storage object for platform metadata
 * @param localStorageKey
 * @param metaData
 * @returns
 */
export const loadLocalStorageMetadata = (localStorageKey: string, metaData: PlatformScheduleMetadataDTO[]) => {
  const storedMetadata = localStorage.getItem(localStorageKey);

  return storedMetadata ? JSON.parse(storedMetadata) : metaData;
};

/**
 * Method to load auto-post toggle value from localStorage with truthy default
 * @param localStorageKey
 * @param platformIsEnabled
 * @returns
 */
export const loadLocalAPToggleValue = (localStorageKey: string, platformIsEnabled: boolean | undefined, autoPostAllowed: boolean) => {
  if (autoPostAllowed === false) return false;

  const storedAPToggleValue = localStorage.getItem(localStorageKey);
  let localAPToggleValue = storedAPToggleValue ? JSON.parse(storedAPToggleValue) : null;
  if (localAPToggleValue === null) {
    localAPToggleValue = platformIsEnabled === undefined ? autoPostAllowed : platformIsEnabled;
  }
  return localAPToggleValue ? localAPToggleValue : false;
};

/**
 * Method to determine equality between two arrays of objects
 * @param firstArr
 * @param secondArr
 * @returns
 */
export const areObjectArraysEqual = (firstArr: Record<any, any>, secondArr: Record<any, any>) => {
  return (
    firstArr.length === secondArr.length &&
    firstArr.every((element_1: any) => secondArr.some((element_2: any) => Object.keys(element_1).every((key) => element_1[key] === element_2[key])))
  );
};

/**
 * Method to get the post type
 * @param postTypes
 * @param name
 * @returns
 */
export const getPlatformPostTypeByName = (postTypes: any, name: string): any => {
  let found = null;
  postTypes.forEach((type: any) => {
    if (kebabCaseString(type.name) === name) {
      found = type;
    }
  });

  return found;
};

/**
 * Method to filter duplicate objects from an array
 * @param arrayToFilter
 * @returns
 */
export const removeDuplicateObjectsFromArray = (arrayToFilter: any[]) => {
  return arrayToFilter.filter((item, index) => {
    const currentItem = JSON.stringify(item);
    return (
      index ===
      arrayToFilter.findIndex((obj) => {
        return JSON.stringify(obj) === currentItem;
      })
    );
  });
};

/**
 * Method to parse the incoming text and return the number or hashtags
 * @param text
 * @retuns int
 */
export const parseHashtags = (text: string) => {
  if (!text) return 0;

  // eslint-disable-next-line no-useless-escape
  const regex = /\#\w+/g;
  const items = text.match(regex);

  return items ? items.length : 0;
};

/**
 * Method to parse the incoming text and return the number of user tags
 * @param text
 * @returns int
 */
export const parseUserTags = (text: string) => {
  if (!text) return 0;

  // eslint-disable-next-line no-useless-escape
  const regex = /\@\w+/g;
  const items = text.match(regex);

  return items ? items.length : 0;
};

/**
 * Method to get thumbnail icon path based upon a given file type
 * @param fileType
 * @returns
 */
export const getThumbnailIcon = (fileType: string) => {
  let iconPath;
  switch (fileType) {
    case 'image':
      iconPath = '../assets/images/scp-icon-image-asset.svg';
      break;
    case 'video':
      iconPath = '../assets/images/scp-icon-video-asset.svg';
      break;
    case 'error':
      iconPath = '../assets/images/scp-icon-asset-error.svg';
      break;
    default:
      console.log('Unable to determine file type');
      break;
  }
  return iconPath;
};

/**
 * Method to validate number of assets for current block card according to rule in config
 * @param assets
 * @param postType
 * @returns
 */
export const hasValidNumBlockCardAssets = (assets: PlatformScheduleAssetDTO[], postType: any) => {
  const rules = postType.validations;

  const rule = rules.find((item: any) => item.type === 'numberOfAssets');
  if (!rule) return [];

  const { min, max } = rule.parameters;

  if (assets.length && !validateMinMaxInclusive(assets.length, min, max)) {
    return [
      {
        id: 'numberOfAssets',
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
  }

  return [];
};

/**
 * Method to validate file size for a single asset according to a rule in config
 * @param file
 * @param postType
 * @returns
 */
export const hasValidFileSize = (file: File, postType: any) => {
  const rules = postType.validations;
  const rule = rules.find((item: any) => item.type === 'autoPostVideoSize');
  if (!rule) return true;

  const { value } = rule.parameters;

  return file?.size && validateFileSize(file.size / 1000000, value);
};

/**
 * Method to simply return the autoPostVideoSize error
 * @param postType
 * @returns
 */
export const getFileSizeError = (postType: any) => {
  const rules = postType.validations;

  const rule = rules.find((item: any) => item.type === 'autoPostVideoSize');
  if (!rule) return [];

  const { value } = rule.parameters;

  if (value) {
    return [
      {
        id: 'autoPostVideoSize',
        message: rule.userErrorMessage,
        link: rule.errorHelpLink
      }
    ];
  }

  return [];
};

/**
 * Method to check if uploaded item is a valid asset
 * @param item
 * @returns
 */
export const isValidAsset = (item: any) => {
  if (typeof item === 'string') {
    return [
      {
        id: `asset`,
        message: item
      }
    ];
  }
  return [];
};

/**
 * Method to validate that uploaded item is an allowed type
 * @param items
 * @param allowedMediaTypes array of mime types e.g. text/plain
 * @returns
 */
export const isAllowedType = (items: any, allowedMediaTypes: string[]) => {
  if (
    !validateAllowedTypes(
      items.map((item: { contentType: any }) => item.contentType),
      allowedMediaTypes
    )
  ) {
    return [
      {
        id: 'mediaType',
        message: 'Error: Please only upload allowed media types'
      }
    ];
  }
  return [];
};

/**
 * Method to validate on uploaded item
 * @param item
 * @param allowedMediaTypes array of mime types e.g. text/plain
 * @param assets
 * @param postType
 * @param errors
 * @param assetElms
 * @param platformSchedule
 * @returns
 */
export const validateUploadedMedia = (
  item: any,
  allowedMediaTypes: string[],
  assets: PlatformScheduleAssetDTO[],
  postType: { validations: any[] },
  errors: any[],
  assetElms: (HTMLVideoElement | HTMLImageElement)[],
  platformSchedule: PlatformScheduleDTO
) => {
  let errorList = [...errors];
  const warningList: string[] = [];
  if (item) {
    errorList = [...errorList, ...isValidAsset(item)];
  }
  // validate that the item is of the correct asset type
  if (item && allowedMediaTypes) {
    errorList = [...errorList, ...isAllowedType(assets, allowedMediaTypes)];
  }
  // validate the post has the correct number of assets they can upload
  if (assets && postType) {
    errorList = [...errorList, ...hasValidNumBlockCardAssets(assets, postType)];
  }
  if (postType && assetElms.length) {
    if (postType.validations.find((validation) => validation.type === 'aspectRatioRange') && !platformSchedule.isEnabled) {
      errorList = [...validateAspectRatioRange(assetElms, postType)];
    } else if (postType.validations.find((validation) => validation.type === 'aspectRatio') && platformSchedule.isEnabled) {
      errorList = [...errorList, ...validateAspectRatio(assetElms, postType)];
    }
  }

  errorList = [...errorList, ...validateMediaFileSize(item.size, item.type, postType)];
  return {
    errors: removeDuplicateObjectsFromArray(errorList),
    warnings: removeDuplicateObjectsFromArray(warningList)
  };
};

/**
 * Method to filter out assets for saving if they have switched back and forth from inline to block style
 * and they have uploaded multiple assets in block and then swtiched to inline
 * @param assetData
 * @param selectedPostType
 * @returns
 */
export const filterAssetsForSave = (assetData: PlatformScheduleAssetDTO[], selectedPostType: any) => {
  if (selectedPostType.allowMultipleAssets === true || assetData.length === 0) {
    return assetData;
  }

  // remove the first item in the array and return as an array of 1
  return [assetData.shift()];
};

/**
 * Method to drive the layout based on the postType.allowMultipleAssets
 * @param postType
 * @returns
 */
export const getCardStyleFromConfig = (postType: any) => {
  let style = 'inline';
  if (postType.allowMultipleAssets === true) {
    style = 'block';
  }
  return style;
};

/**
 * Method to move item from one index in array to another
 * @param array in which the item to move exists
 * @param to index to which it should be moved
 * @param from index from which it is moving
 * @returns
 */
export const moveItem = (array: PlatformScheduleAssetDTO[], to: number, from: number) => {
  const arrayClone = [...array];
  const itemToMove = arrayClone[from];
  arrayClone.splice(from, 1);
  arrayClone.splice(to, 0, itemToMove);
  return arrayClone;
};

/**
 * Method to render the correct formatting of the platform name
 * @param name
 * @returns string
 */
export const formatName = (name: string) => {
  switch (name) {
    case 'tiktok':
      return 'TikTok';
    case 'youtube':
      return 'YouTube';
    case 'linkedin':
      return 'LinkedIn';
    default:
      return name.charAt(0).toUpperCase() + name.slice(1);
  }
};

/**
 * Method to get the type of asset
 * NOTE: the numbers match what is stored in the DB
 * @param type
 * @returns
 */
export function getItemTypeId(type: string[]) {
  // 1 - image
  // 2 - video
  if (type.includes('video')) {
    return 2;
  }
  return 1;
}

/**
 * Method to get the caption length depending on the rule of the validations
 * @param validations
 * @param isEnabled
 * @returns
 */
export function getCaptionMaxLength(validations: any, isEnabled: boolean) {
  let max = 0;
  let rule = null;
  if (isEnabled) {
    rule = validations.filter((validation: any) => validation.type === 'autoPostCaptionLength');
  } else {
    rule = validations.filter((validation: any) => validation.type === 'manualPostCaptionLength');
  }
  // If the rule is found
  if (rule.length) {
    max = rule[0].parameters.max;
  }
  return max;
}

/**
 * Method to get the caption min length depending on the rule of the validations
 * @param validations
 * @param isEnabled
 * @returns
 */
export function getCaptionMinLength(validations: any, isEnabled: boolean) {
  let min = 0;
  let rule = null;
  if (isEnabled) {
    rule = validations.filter((validation: any) => validation.type === 'autoPostCaptionLength');
  } else {
    rule = validations.filter((validation: any) => validation.type === 'manualPostCaptionLength');
  }
  // If the rule is found
  if (rule.length) {
    min = rule[0].parameters.min;
  }
  return min;
}

/**
 * Method to get the first comment length depending on the rule of the validations
 * @param validations
 * @param isEnabled
 * @returns
 */
export function getFirstCommentMaxLength(validations: any) {
  let max = 0;
  const rule = validations.filter((validation: any) => validation.type === 'firstCommentLength');
  // If the rule is found
  if (rule.length) {
    max = rule[0].parameters.max;
  }
  return max;
}

/**
 * Method to get the title length depending on the rule of the validations
 * @param validations
 * @param isEnabled
 * @returns
 */
export function getTitleMaxLength(validations: any, maxFallback = 100) {
  let max = 0;
  let rule = null;
  rule = validations.filter((validation: any) => validation.type === 'titleLength');
  // This violation pre-dates the introduction of @typescript-eslint/no-unused-vars
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return (max = rule.length ? rule[0].parameters.max : maxFallback);
}

/**
 * Method to get the title min length depending on the rule of the validations
 * @param validations
 * @param isEnabled
 * @returns
 */
export function getTitleMinLength(validations: any) {
  let rule = null;
  rule = validations.filter((validation: any) => validation.type === 'titleLength');
  return rule.length ? rule[0].parameters.min : 0;
}

/**
 * Method to get the hashtag limit depending on the rule of the validations
 * @param validations
 * @param isEnabled
 * @returns
 */
export function getHashtagLimit(validations: any, maxFallback = 30) {
  let max = 0;
  let rule = null;
  rule = validations.filter((validation: any) => validation.type === 'hashtagLimit');
  // This violation pre-dates the introduction of @typescript-eslint/no-unused-vars
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return (max = rule.length ? rule[0].parameters.max : maxFallback);
}

/**
 * Method to get the first comment mention limit depending on the rule of the validations
 * @param validations
 * @param isEnabled
 * @returns
 */
export function getFirstCommentMentionLimit(validations: any, maxFallback = 10) {
  let max = 0;
  let rule = null;
  rule = validations.filter((validation: any) => validation.type === 'firstCommentMentionLimit');
  // This violation pre-dates the introduction of @typescript-eslint/no-unused-vars
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return (max = rule.length ? rule[0].parameters.max : maxFallback);
}

/**
 * Method to get the platform options key
 * @param name
 */
export function getPlatformOptionsKey(name: string) {
  if (name === 'pinterest') {
    return 'board';
  }
  return 'platformOptions';
}

/**
 * Method to get a value from metadata
 * @param metadata
 * @param isFeatureEnabled
 * @param name
 * @returns
 */
export function getValueFromMetaData(metadata: PlatformScheduleMetadataDTO[], isFeatureEnabled: boolean, name = 'soundUrl') {
  if (!isFeatureEnabled || !metadata || metadata.length === 0) {
    return null;
  }
  const item = metadata.filter((item) => item.name === name);
  if (item.length) {
    return item[0].value;
  }
  return null;
}

/**
 *  Method to update a value from metadata
 * @param metadata
 * @param name
 * @param newValue
 * @returns
 */
export function updateMetaDataValue(metadata: any[], name: string, newValue: boolean) {
  if (!metadata || metadata.length === 0) {
    metadata = [];
  }
  const item = metadata?.find((item) => item.name === name);
  if (item) {
    item.value = newValue;
  } else {
    metadata.push({
      name: name,
      value: newValue
    });
  }

  return metadata;
}

/**
 *  Method to get eligible assets for transfer on post type change based
 *  upon the destination types allowable media types
 * @param destinationAllowedMediaTypes
 * @param assets
 * @returns array of eligible assets
 */
export function getTransferableAssets(destinationAllowedMediaTypes: any, assets: PlatformScheduleAssetDTO[]) {
  return assets.filter((asset) => destinationAllowedMediaTypes.includes(asset.contentType));
}

/**
 *  Method to get the base media type (i.e. image or video)
 *  based upon the full content type
 * @param contentType
 * @returns string
 */
export function getBaseMediaType(contentType: string) {
  const regex = /^[^/]*/g;
  return contentType.match(regex)?.[0];
}

/**
 *  Method to get array of base media types (i.e. image or video) from set of assets or supported media types
 * @param assets
 * @param supportedMediaTypes
 * @returns array of base media types
 */
export function getBaseMediaTypes(assets: PlatformScheduleAssetDTO[], supportedMediaTypes: string[] = []) {
  const baseMediaTypes = assets ? assets.map((asset) => getBaseMediaType(asset.contentType)) : supportedMediaTypes.map((fullType) => getBaseMediaType(fullType));
  return [...new Set(baseMediaTypes)];
}

/**
 *  Method to capitalize the first letter of every word in a string
 * @param string
 * @returns string
 */
export function capitalize(string: string) {
  return string.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());
}

/**
 *  Very basic means of pluralizing a given word
 * @param word
 * @returns string
 */
export function pluralize(word: string) {
  const normalizedWord: string = word.toLowerCase();
  const dict: Record<string, string> = {
    story: 'stories'
  };
  return dict?.[normalizedWord] ?? `${normalizedWord}s`;
}

/**
 *  Method to generate text for media transfer modal
 * @param transferableAssets
 * @param destinationPostType
 * @param sourceUploadedAssets
 * @returns object
 */
export function generateTransferModalData(transferableAssets: PlatformScheduleAssetDTO[], destinationPostType: any, sourceUploadedAssets: PlatformScheduleAssetDTO[]) {
  let modalPrimaryText, modalSecondaryText, affirmButtonText, dismissButtonText, action;
  const { name: destinationPostTypeName, allowedMediaTypes: destinationAllowedMediaTypes } = destinationPostType;
  const destinationPostTypeNamePlural = pluralize(destinationPostTypeName);
  const destinationSupportedMediaTypes = getBaseMediaTypes(null, destinationAllowedMediaTypes);
  // if no transferable assets, this is a discard modal
  if (transferableAssets.length < 1) {
    const sourceNumUploadedAssets = sourceUploadedAssets.length;
    const sourceUploadedUnsupportedMediaTypes = getBaseMediaTypes(sourceUploadedAssets)
      .map((type) => (sourceNumUploadedAssets > 1 ? pluralize(type) : type))
      .join('& ');
    modalPrimaryText = `${capitalize(destinationPostTypeNamePlural)} only support ${pluralize(
      destinationSupportedMediaTypes[0]
    )}. Your uploaded ${sourceUploadedUnsupportedMediaTypes} will be discarded. Would you like to continue?`;
    modalSecondaryText = `You currently have ${sourceNumUploadedAssets} ${sourceUploadedUnsupportedMediaTypes} uploaded.`;
    affirmButtonText = 'Continue';
    dismissButtonText = 'Cancel';
    action = 'discard';
  } else {
    modalPrimaryText = `You are changing your post type to ${capitalize(destinationPostTypeName)}. Would you like to keep one of the media you’ve uploaded?`;
    affirmButtonText = 'Select Media';
    dismissButtonText = 'Don’t keep media';
    action = 'select';
    if (transferableAssets.length === 1) {
      modalSecondaryText = 'Please confirm media transfer!';
      affirmButtonText = 'Transfer Media';
      action = 'transfer';
    } else {
      modalSecondaryText =
        destinationSupportedMediaTypes.length > 1
          ? `${capitalize(destinationPostTypeNamePlural)} currently support 1 video or image. You will see both to select from.`
          : `${capitalize(destinationPostTypeNamePlural)} currently support only 1 ${destinationSupportedMediaTypes[0]} and not ${pluralize(
              destinationSupportedMediaTypes[0] === 'image' ? 'video' : 'image'
            )}. You will only see your ${pluralize(destinationSupportedMediaTypes[0])} to select from.`;
    }
  }
  return {
    destinationPostType,
    transferableAssets,
    modalPrimaryText,
    modalSecondaryText,
    affirmButtonText,
    dismissButtonText,
    action
  };
}
